import { useState } from 'react'
import {
  ChevronDown, Send, FileText, Bot, CheckCircle,
  Layers, Tag, Globe, Copy, Settings, Sparkles,
  AlertTriangle, Info, ArrowRight, Eye, EyeOff,
  DollarSign, Languages, Wrench, Image, ClipboardList, Briefcase
} from 'lucide-react'
import AnchorLink from './AnchorLink'

const SubSectionHeading = ({ children, id }) => (
  <div className="mb-6" id={id}>
    <div className="group flex items-center gap-2">
      <h3 className="wiki-heading text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
        {children}
      </h3>
      {id && <AnchorLink id={id} />}
    </div>
    <div className="mt-2 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
)

const Step = ({ number, icon: Icon, title, children }) => (
  <div className="relative pl-14 py-2">
    <span aria-hidden="true" className="wiki-mono absolute left-0 top-0 text-6xl font-bold leading-none select-none text-light-border-primary dark:text-dark-bg-tertiary">
      {number}
    </span>
    <div className="absolute left-10 top-2 bottom-2 w-0.5 bg-primary-500 rounded-full" />
    <div className="pt-1">
      <h5 className="wiki-heading text-base font-semibold text-light-text-primary dark:text-dark-text-primary mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-primary-500 flex-shrink-0" />}
        {title}
      </h5>
      {children}
    </div>
  </div>
)

const InfoBox = ({ children, variant = 'default' }) => {
  const styles = {
    default: 'bg-light-bg-secondary dark:bg-dark-bg-secondary border-light-border-primary dark:border-dark-border-primary',
    tip:     'bg-primary-50/50 dark:bg-primary-900/10 border-primary-500/30',
    warning: 'bg-amber-50 dark:bg-amber-900/10 border-amber-300 dark:border-amber-700',
  }
  return (
    <div className={`mt-3 rounded-lg p-3 border ${styles[variant]}`}>
      {children}
    </div>
  )
}

const Tag_ = ({ children, color = 'gray' }) => {
  const colors = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    green:  'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    amber:  'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    gray:   'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary border-light-border-primary dark:border-dark-border-primary',
  }
  return (
    <span className={`wiki-mono inline-flex items-center text-[11px] px-1.5 py-0.5 rounded border ${colors[color]}`}>
      {children}
    </span>
  )
}

const StatusBadge = ({ label, color }) => {
  const colors = {
    gray:   'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary',
    green:  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    amber:  'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  }
  return (
    <span className={`wiki-mono text-[11px] font-medium px-2 py-0.5 rounded-full ${colors[color]}`}>
      {label}
    </span>
  )
}

const faqItems = [
  {
    key: 'posting-q1',
    question: 'Can a post be created without AI generation?',
    answer: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        Yes. The <strong>full_post</strong> field can be filled in by hand — just type the post text directly into the edit form. AI generation is optional.
      </p>
    )
  },
  {
    key: 'posting-q2',
    question: 'What is a slug and why is it needed?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>A slug is the post's unique text identifier in the URL, for example <Tag_ color="gray">john-doe-en-linkedin</Tag_>. It is used in the public catalog: <Tag_ color="gray">/feed/john-doe-en-linkedin</Tag_></p>
        <p>It is generated automatically from the post name (Cyrillic is transliterated), but you can set it manually. Uniqueness is checked in real time.</p>
      </div>
    )
  },
  {
    key: 'posting-q3',
    question: 'How does "unpublished" differ from "draft"?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p><Tag_ color="gray">draft</Tag_> — the post has never been published. This is the initial state.</p>
        <p><Tag_ color="amber">unpublished</Tag_> — the post was published and then taken down. Content and data are kept, the post is simply hidden from the public catalog. It can be published again at any time.</p>
      </div>
    )
  },
  {
    key: 'posting-q4',
    question: 'What are "terms" — main and similar — for?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>The public catalog relies on posts being filtered correctly by profession. The system warns you when the chosen term is already used in another published post of the same talent — in that case pick a <strong>similar term</strong> so that the posts do not compete with each other in the filters.</p>
        <p>One term is marked as the <strong>Primary profession</strong> — it is shown as the main one in the post.</p>
      </div>
    )
  },
  {
    key: 'posting-q5',
    question: 'How does automatic Prompt Template matching work?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>When a template is created, the system looks for the most suitable Prompt Template, from the most specific match to the most general:</p>
        <ol className="space-y-1 ml-4 list-decimal">
          <li>Exact match on platform <strong>and</strong> client</li>
          <li>Platform matches only</li>
          <li>Client matches only</li>
          <li>The global default prompt (<Tag_ color="gray">is_default = true</Tag_>)</li>
        </ol>
        <p>If nothing matches, pick a prompt manually or create a new one.</p>
      </div>
    )
  },
  {
    key: 'posting-q6',
    question: 'What is a "Revision" in AI generation?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>After the text is generated you can press <strong>Revise</strong> and write a comment — what to change, rephrase or add. The system sends the original result together with your feedback back to the AI.</p>
        <p>The revision history is kept for the editing session, so you can run several iterations in a row.</p>
      </div>
    )
  },
  {
    key: 'posting-q7',
    question: 'Can a post be created straight from the talent profile?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>Yes. The talent profile has a <strong>Posts</strong> tab (visible with the right permissions). It lists every post of that talent and has a button to create a new one — the talent is filled in automatically.</p>
      </div>
    )
  },
]

const TalentPostingGuide = () => {
  const [expandedBlocks, setExpandedBlocks] = useState({})
  const toggle = (key) => setExpandedBlocks(prev => ({ ...prev, [key]: !prev[key] }))

  return (
    <div className="space-y-10">

      {/* Overview */}
      <div>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed mb-5">
          Talent Posting is the system for building and publishing talent cards for an outside audience.
          A post is assembled from the employee profile with the content blocks you need, the AI writes the
          text from a prompt, and the post is then published to the public catalog or to a specific platform.
        </p>

        {/* Three entities */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {
              icon: FileText,
              color: 'text-blue-600 dark:text-blue-400',
              bg: 'bg-blue-50 dark:bg-blue-900/20',
              border: 'border-blue-200 dark:border-blue-800',
              title: 'Talent Template',
              desc: 'The post blueprint: platform, client, prompt and the set of content blocks. Reused many times.'
            },
            {
              icon: Bot,
              color: 'text-purple-600 dark:text-purple-400',
              bg: 'bg-purple-50 dark:bg-purple-900/20',
              border: 'border-purple-200 dark:border-purple-800',
              title: 'Prompt Template',
              desc: 'The instruction for the AI: tone, structure, what to include. Attached to a template.'
            },
            {
              icon: Send,
              color: 'text-green-600 dark:text-green-400',
              bg: 'bg-green-50 dark:bg-green-900/20',
              border: 'border-green-200 dark:border-green-800',
              title: 'Talent Post',
              desc: 'One specific post for one specific talent. Has content, a slug and a publish status.'
            },
          ].map(e => {
            const Icon = e.icon
            return (
              <div key={e.title} className={`p-4 rounded-xl border ${e.border} ${e.bg}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${e.color}`} />
                  <span className={`wiki-heading text-sm font-semibold ${e.color}`}>{e.title}</span>
                </div>
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">{e.desc}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Step 1: Prompt Template */}
      <div>
        <SubSectionHeading id="posting-prompt">Step 1 — Create a Prompt Template</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-5 leading-relaxed">
          A prompt template is the instruction for the AI: how to write, which tone to use, what to emphasise.
          You create it once and reuse it across several Talent Templates.
        </p>

        <div className="space-y-8">
          <Step number="1" icon={Layers} title="Open the Prompts section in the sidebar">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              It sits in the <strong>Talent Posting</strong> group in the left menu. Click <strong>Add Prompt</strong>.
            </p>
          </Step>

          <Step number="2" icon={Settings} title="Fill in the form">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              Prompt fields:
            </p>
            <div className="space-y-2">
              {[
                { f: 'Name', r: true,  d: 'Generated from platform + client, can be set manually' },
                { f: 'Body', r: true,  d: 'The instruction text for the AI. Variables can be inserted (see below)' },
                { f: 'Platform (Tool)', r: false, d: 'The platform this prompt is written for (LinkedIn, Telegram, etc.)' },
                { f: 'Inner Client', r: false, d: 'The client the prompt is tailored to' },
                { f: 'Is Default', r: false, d: 'Mark as default — used when there is no exact match' },
              ].map(({ f, r, d }) => (
                <div key={f} className="flex items-start gap-2 text-xs">
                  <span className={`mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${r ? 'bg-primary-500' : 'bg-light-text-tertiary dark:bg-dark-text-tertiary'}`} />
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className={r ? 'font-medium text-light-text-primary dark:text-dark-text-primary' : ''}>{f}</span>
                    {' — '}{d}
                  </span>
                </div>
              ))}
            </div>
          </Step>

          <Step number="3" icon={Sparkles} title="Use variables in the Body (optional)">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              The prompt text can contain placeholders — the system fills in the talent's data automatically:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { v: '{{talent_name}}',         d: 'Full name' },
                { v: '{{company_name}}',         d: 'Company name' },
                { v: '{{company_description}}',  d: 'Company description' },
                { v: '{{professions}}',           d: 'List of professions' },
                { v: '{{responsibilities}}',      d: 'Responsibilities' },
                { v: '{{tools}}',                d: 'Skills and tools' },
                { v: '{{languages}}',             d: 'Languages' },
                { v: '{{experience}}',            d: 'CV / work experience' },
                { v: '{{media}}',                d: 'Portfolio links' },
              ].map(({ v, d }) => (
                <div key={v} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary">
                  <Tag_ color="purple">{v}</Tag_>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">{d}</span>
                </div>
              ))}
            </div>
            <InfoBox variant="tip">
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                <strong className="text-light-text-primary dark:text-dark-text-primary">Without variables:</strong> all talent data is appended to the end of the prompt as structured sections automatically.{' '}
                <strong className="text-light-text-primary dark:text-dark-text-primary">With variables:</strong> you control the structure yourself — data goes exactly where the placeholders are.
              </p>
            </InfoBox>
          </Step>
        </div>
      </div>

      {/* Step 2: Talent Template */}
      <div>
        <SubSectionHeading id="posting-template">Step 2 — Create a Talent Template</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-5 leading-relaxed">
          A Talent Template configures a whole group of posts: which platform and client they belong to,
          which prompt to use, and which content blocks go into the generation.
          One template serves many posts.
        </p>

        <div className="space-y-8">
          <Step number="1" icon={FileText} title="Open the Templates section in the sidebar">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              Go to <strong>Talent Posting → Templates</strong>. Click <strong>Add Template</strong>.
            </p>
          </Step>

          <Step number="2" icon={Settings} title="Fill in the form">
            <div className="space-y-2 mb-3">
              {[
                { f: 'Name',          r: true,  d: 'Generated from platform + client' },
                { f: 'Status',        r: true,  d: 'Template status (Active, for example)' },
                { f: 'Prompt',        r: true,  d: 'The prompt template used for AI generation' },
                { f: 'Platform',      r: false, d: 'Publishing platform (LinkedIn, Telegram, etc.)' },
                { f: 'Inner Client',  r: false, d: 'Client — fills in the ISO code automatically' },
                { f: 'Is Draft',      r: false, d: 'Hides the template from the picker when creating posts' },
              ].map(({ f, r, d }) => (
                <div key={f} className="flex items-start gap-2 text-xs">
                  <span className={`mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${r ? 'bg-primary-500' : 'bg-light-text-tertiary dark:bg-dark-text-tertiary'}`} />
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className={r ? 'font-medium text-light-text-primary dark:text-dark-text-primary' : ''}>{f}</span>
                    {' — '}{d}
                  </span>
                </div>
              ))}
            </div>
          </Step>

          <Step number="3" icon={Layers} title="Configure the Content Blocks">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              Blocks decide which talent data reaches the AI generation and the finished post.
              Turn on only what the particular platform needs:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { icon: Briefcase,    label: 'Professions',     default: true },
                { icon: ClipboardList,label: 'Responsibilities',default: true },
                { icon: Wrench,       label: 'Tools',           default: true },
                { icon: Languages,    label: 'Languages',       default: true },
                { icon: FileText,     label: 'CV / Experience', default: true },
                { icon: Image,        label: 'Media',           default: false },
              ].map(b => {
                const Icon = b.icon
                return (
                  <div key={b.label} className="flex items-center gap-2 p-2 rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary text-xs">
                    <Icon className="w-3.5 h-3.5 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0" />
                    <span className="text-light-text-secondary dark:text-dark-text-secondary flex-1">{b.label}</span>
                    <span className={`wiki-mono text-[10px] ${b.default ? 'text-green-600 dark:text-green-400' : 'text-light-text-tertiary dark:text-dark-text-tertiary'}`}>
                      {b.default ? 'on' : 'off'}
                    </span>
                  </div>
                )
              })}
            </div>
            <InfoBox>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0 mt-0.5" />
                Template blocks are just defaults. They can be overridden on each individual post.
              </p>
            </InfoBox>
          </Step>
        </div>

        <InfoBox variant="tip">
          <p className="text-xs font-medium text-light-text-primary dark:text-dark-text-primary mb-1 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-primary-500" /> Automatic prompt matching
          </p>
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
            Pick a platform and/or a client and the system suggests a matching prompt: first an exact match
            (platform + client), then a match on one criterion, then the default one.
          </p>
        </InfoBox>
      </div>

      {/* Step 3: Create Post */}
      <div>
        <SubSectionHeading id="posting-create-post">Step 3 — Create a Talent Post</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-5 leading-relaxed">
          A post is one specific card for one specific talent, built from a template.
          Create it from the <strong>Posts</strong> section in the sidebar, or straight from the <strong>Posts</strong> tab in the talent profile.
        </p>

        <div className="space-y-8">
          <Step number="1" icon={Send} title="Open the post creation form">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              <strong>Talent Posting → Posts → Add Post</strong>, or the <strong>Posts</strong> tab in the talent profile (the talent is then filled in automatically).
            </p>
          </Step>

          <Step number="2" icon={Settings} title="Fill in the main fields">
            <div className="space-y-2 mb-3">
              {[
                { f: 'Template',      r: true,  d: 'The template — fills in the client and the blocks' },
                { f: 'Talent',        r: true,  d: 'Whose card you are building' },
                { f: 'Name',          r: true,  d: 'Generated as: name | client | language | platform' },
                { f: 'Slug',          r: true,  d: 'URL identifier. Generated automatically, checked for uniqueness' },
                { f: 'Status',        r: true,  d: 'Post status (Draft by default)' },
                { f: 'Inner Client',  r: true,  d: 'Taken from the template, can be changed' },
                { f: 'Translation',   r: true,  d: 'Generation language (English by default)' },
                { f: 'Account',       r: false, d: 'Recipient — an account or a social account' },
                { f: 'Planned Date',  r: false, d: 'Planned publication date' },
                { f: 'End Date',      r: false, d: 'The date the post stops being relevant' },
                { f: 'Discount',      r: false, d: 'Discount in % (a negative value means a markup)' },
              ].map(({ f, r, d }) => (
                <div key={f} className="flex items-start gap-2 text-xs">
                  <span className={`mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${r ? 'bg-primary-500' : 'bg-light-text-tertiary dark:bg-dark-text-tertiary'}`} />
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className={r ? 'font-medium text-light-text-primary dark:text-dark-text-primary' : ''}>{f}</span>
                    {' — '}{d}
                  </span>
                </div>
              ))}
            </div>
          </Step>

          <Step number="3" icon={Tag} title="Set up professions and terms">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              For every profession and responsibility of the talent you pick a term — <strong>main</strong> or <strong>similar</strong>.
              This drives filtering in the public catalog.
            </p>
            <div className="space-y-2">
              <div className="flex gap-2 items-start text-xs p-2.5 rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
                <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Primary profession</span>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary"> — one profession is marked as the main one in the post</span>
                </div>
              </div>
              <div className="flex gap-2 items-start text-xs p-2.5 rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Duplicate warning</span>
                  <span className="text-light-text-secondary dark:text-dark-text-secondary"> — if the term is already used in another published post of this talent, the system suggests picking a similar term instead</span>
                </div>
              </div>
            </div>
          </Step>

          <Step number="4" icon={DollarSign} title="Set prices (optional)">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              Each profession can carry a custom price (rate) instead of the base one from the talent profile.
              You can choose the currency and the rate type (hourly, monthly, etc.).
              Leave it empty and the base data is used.
            </p>
          </Step>
        </div>
      </div>

      {/* Step 4: Generate */}
      <div>
        <SubSectionHeading id="posting-generate">Step 4 — Generate the content with AI</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-5 leading-relaxed">
          Once the post exists, the text can be generated by AI.
          The system gathers the talent's data, applies the prompt from the template and returns finished text.
        </p>

        <div className="space-y-8">
          <Step number="1" icon={Bot} title='Click "Generate with AI" in the post edit form'>
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-2">
              The button lives in the edit modal. You can pick the AI model (Gemini 2.0 Flash by default).
            </p>
            <InfoBox>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                What goes in: talent data + the selected professions and responsibilities + content blocks + the prompt + the translation language.
              </p>
            </InfoBox>
          </Step>

          <Step number="2" icon={Eye} title="Review the draft">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
              The generated text is shown read-only. Three actions are available:
            </p>
            <div className="space-y-2">
              {[
                { icon: CheckCircle, color: 'text-green-500', action: 'Accept', desc: 'Take the text — it is saved into the full_post field' },
                { icon: Sparkles,    color: 'text-blue-500',  action: 'Revise', desc: 'Write a comment and ask for a rework (iterative revision)' },
                { icon: EyeOff,     color: 'text-red-500',   action: 'Discard', desc: 'Throw the draft away, the post content stays as it was' },
              ].map(r => {
                const Icon = r.icon
                return (
                  <div key={r.action} className="flex gap-2 items-start text-xs p-2.5 rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
                    <Icon className={`w-3.5 h-3.5 ${r.color} flex-shrink-0 mt-0.5`} />
                    <div>
                      <span className="font-medium text-light-text-primary dark:text-dark-text-primary">{r.action}</span>
                      <span className="text-light-text-secondary dark:text-dark-text-secondary"> — {r.desc}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </Step>

          <Step number="3" icon={CheckCircle} title="Save the post">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              After accepting the draft, save the post. The <strong>full_post</strong> field is now filled in — the post is ready to publish.
              You can still edit the text by hand if needed.
            </p>
          </Step>
        </div>
      </div>

      {/* Step 5: Publish */}
      <div>
        <SubSectionHeading id="posting-publish">Step 5 — Publish the post</SubSectionHeading>

        <div className="space-y-8 mb-6">
          <Step number="1" icon={Send} title='Click "Publish" in the posts list'>
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-2">
              The table in the <strong>Posts</strong> section has a publish button next to every post.
              It is only enabled once <strong>full_post</strong> is filled in.
            </p>
            <InfoBox variant="warning">
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                Without content (full_post) publishing is impossible — the button stays disabled.
              </p>
            </InfoBox>
          </Step>

          <Step number="2" icon={Globe} title="Fill in the publication details">
            <div className="space-y-2 mt-2">
              {[
                { f: 'Link',         r: false, d: 'URL where the post is placed (LinkedIn, a website, etc.)' },
                { f: 'Publish Date', r: true,  d: 'Publication date (today by default)' },
              ].map(({ f, r, d }) => (
                <div key={f} className="flex items-start gap-2 text-xs">
                  <span className={`mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${r ? 'bg-primary-500' : 'bg-light-text-tertiary dark:bg-dark-text-tertiary'}`} />
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">
                    <span className={r ? 'font-medium text-light-text-primary dark:text-dark-text-primary' : ''}>{f}</span>
                    {' — '}{d}
                  </span>
                </div>
              ))}
            </div>
          </Step>

          <Step number="3" icon={CheckCircle} title="Confirm — the post goes to the public catalog">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              After publishing, <Tag_ color="green">publish_status = published</Tag_>. The post appears in the public catalog at <Tag_ color="gray">/feed/{`{slug}`}</Tag_>.
              A Discord notification is sent, if one is configured.
            </p>
          </Step>
        </div>

        {/* Publish statuses */}
        <div>
          <p className="wiki-mono text-[11px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-3">Publish statuses</p>
          <div className="space-y-2">
            {[
              { status: 'draft',       badge: 'draft',       color: 'gray',  desc: 'The initial state. The post is not visible publicly. Editable.' },
              { status: 'published',   badge: 'published',   color: 'green', desc: 'The post is visible in the public catalog. Can be taken down.' },
              { status: 'unpublished', badge: 'unpublished', color: 'amber', desc: 'Was published and then taken down. Data is kept, it can be published again.' },
            ].map(r => (
              <div key={r.status} className="flex items-start gap-3 p-3 rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
                <StatusBadge label={r.badge} color={r.color} />
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Duplicate */}
      <div>
        <SubSectionHeading id="posting-duplicate">Duplicating a post</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4 leading-relaxed">
          The posts list has a <strong>Duplicate</strong> button. It copies every setting of the post
          (template, blocks, professions, responsibilities, prices, translation) except the name and the slug —
          those you set yourself. Handy for building a similar post for the same talent on another platform or client.
        </p>
        <div className="flex items-center gap-3 p-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
          <Copy className="w-5 h-5 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0" />
          <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
            Copied: template, talent, client, blocks, professions with their terms, responsibilities, prices, translation, dates, discount.
            <br />
            <span className="text-primary-500 font-medium">Not copied:</span> name, slug, content (full_post), publish status, publication date.
          </div>
        </div>
      </div>

      {/* Public catalog */}
      <div>
        <SubSectionHeading id="posting-catalog">Public catalog</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4 leading-relaxed">
          Every published post that is not tied to a specific account lands in the public catalog.
          The catalog is open without authentication and supports filtering.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'Endpoint', value: '/feed', desc: 'Paginated list of published posts' },
            { label: 'Filters', value: '/feed/filters', desc: 'Available values for every filter' },
            { label: 'Post detail', value: '/feed/{slug}', desc: 'The full post with all blocks and prices' },
            { label: 'Similar', value: '/feed/{slug}/similar', desc: 'Related posts by profession, responsibilities and tools' },
          ].map(e => (
            <div key={e.label} className="p-3 rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
              <div className="flex items-center gap-2 mb-1">
                <span className="wiki-mono text-[10px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary">{e.label}</span>
                <Tag_ color="gray">{e.value}</Tag_>
              </div>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{e.desc}</p>
            </div>
          ))}
        </div>
        <InfoBox>
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
            Catalog filters work off <strong>terms</strong> — professions, responsibilities, tools, languages, rates and departments.
            Posts with similar professions are recommended automatically by score: profession (+3), responsibility (+1), tool (+2).
          </p>
        </InfoBox>
      </div>

      {/* FAQ */}
      <div>
        <SubSectionHeading id="posting-faq">Common Questions</SubSectionHeading>
        <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
          {faqItems.map(({ key, question, answer }) => (
            <div key={key} id={key} className="scroll-mt-6 group">
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button onClick={() => toggle(key)} className="text-sm font-medium text-left text-light-text-primary dark:text-dark-text-primary hover:text-primary-500 transition-colors">
                    {question}
                  </button>
                  <AnchorLink id={key} />
                </div>
                <button onClick={() => toggle(key)} className="flex-shrink-0 ml-4">
                  <ChevronDown className={['w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary transition-transform duration-200', expandedBlocks[key] ? 'rotate-180' : ''].join(' ')} />
                </button>
              </div>
              {expandedBlocks[key] && <div className="pb-4 pr-6">{answer}</div>}
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}

export default TalentPostingGuide
